import { useCallback, useEffect, useState } from 'react';

export function useHandleScroll(
  positionPercentage: number,
  callback: () => boolean | Promise<boolean>,
  shouldAutoReset = true,
): void {
  const [isReset, setIsReset] = useState<boolean>(true);

  const handleScroll = useCallback(
    async function () {
      if (typeof document === 'undefined' || typeof window === 'undefined') {
        return null;
      }
      const scrollHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const scrollPercentage = (scrollTop / scrollHeight) * 100;
      if (isReset && scrollPercentage >= positionPercentage) {
        setIsReset(false);
        const r = await callback();
        setIsReset(r);
      }
      if (
        !isReset &&
        shouldAutoReset &&
        scrollPercentage < positionPercentage
      ) {
        setIsReset(true);
      }
    },
    [isReset, positionPercentage, shouldAutoReset, callback],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  });
}

export function getElementPositionPercentage(element: Element) {
  const rect = element.getBoundingClientRect();
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const elementLeft = rect.left + scrollLeft;
  const elementTop = rect.top + scrollTop;
  const elementRight = rect.right + scrollLeft;
  const elementBottom = rect.bottom + scrollTop;
  const documentWidth = document.documentElement.scrollWidth;
  const documentHeight = document.documentElement.scrollHeight;
  const leftPercentage = (elementLeft / documentWidth) * 100;
  const topPercentage = (elementTop / documentHeight) * 100;
  const rightPercentage = (elementRight / documentWidth) * 100;
  const bottomPercentage = (elementBottom / documentHeight) * 100;
  return {
    leftPercentage,
    topPercentage,
    rightPercentage,
    bottomPercentage,
  };
}
